/*===================================================================
=     use BOX ASSOCIATION           =
====================================================================*/
/**
 * use Box Association 
 * 
 * Give access and manage box association and box data
 */

import { useQuery } from "@tanstack/react-query";
import {
  getSingleBoxAssociationAPI,
  getBoxFromBoxAssociationAPI,
  getBoxAssociationFromBoxIdAPI,
} from "../services/boxAssociationAPI";
import {
  BoxAssociationDescription,
  BoxDescription,
} from "../types/boxAssociation.types";

/**
 * Hook to get a single box association by its ID
 * 
 * @param boxAssoId box association id
 * @param options query options (disableQuery, etc.)
 */
export const useSingleBoxAssociation = (
  boxAssoId: string | undefined,
  options?: { disableQuery?: boolean }
) => {
  const singleQuery = useQuery({
    queryKey: ["box-association", boxAssoId],
    queryFn: () => getSingleBoxAssociationAPI(boxAssoId!),
    enabled: !!boxAssoId && boxAssoId !== "" && !options?.disableQuery,
  });

  return {
    singleQuery,
    boxAssociation: singleQuery.data as BoxAssociationDescription | undefined,
    isLoading: singleQuery.isLoading,
    isError: singleQuery.isError,
    error: singleQuery.error,
  };
};

/**
 * Hook to get box information from box association ID
 * 
 * @param boxAssoId box association id
 * @param options query options (disableQuery, etc.)
 */
export const useBoxFromBoxAssociation = (
  boxAssoId: string | undefined,
  options?: { disableQuery?: boolean }
) => {
  const boxQuery = useQuery({
    queryKey: ["box-from-association", boxAssoId],
    queryFn: () => getBoxFromBoxAssociationAPI(boxAssoId!),
    enabled: !!boxAssoId && boxAssoId !== "" && !options?.disableQuery,
  });

  return {
    boxQuery,
    box: boxQuery.data as BoxDescription | undefined,
    isLoading: boxQuery.isLoading,
    isError: boxQuery.isError,
    error: boxQuery.error,
  };
};

/**
 * Hook to get box association from box ID
 * 
 * @param boxId box id
 * @param options query options (disableQuery, etc.)
 */
export const useBoxAssociationFromBoxId = (
  boxId: string | undefined,
  options?: { disableQuery?: boolean }
) => {
  const boxAssociationQuery = useQuery({
    queryKey: ["box-association-from-box", boxId],
    queryFn: () => getBoxAssociationFromBoxIdAPI(boxId!),
    enabled: !!boxId && boxId !== "" && !options?.disableQuery,
    retry: false, // Ne pas réessayer en cas d'erreur pour éviter les délais
    retryOnMount: false, // Ne pas réessayer au montage
    refetchOnWindowFocus: false, // Ne pas refetch au focus de la fenêtre
  });

  return {
    boxAssociationQuery,
    boxAssociation: boxAssociationQuery.data as BoxAssociationDescription | undefined,
    isLoading: boxAssociationQuery.isLoading,
    isError: boxAssociationQuery.isError,
    error: boxAssociationQuery.error,
  };
};

